import { useEffect } from 'react';
import type { ChannelWithMembership } from '@avslack/shared';
import { isListedChannel } from './channels';

/**
 * Jelvény a telepített alkalmazás ikonján (tálca, dokk, kezdőképernyő).
 *
 * A `setAppBadge` csak TELEPÍTETT alkalmazásnál látszik: sima böngészőfülön a
 * hívás lefut, de nem jelenik meg semmi. Ez nem hiba, és nem is kell külön
 * kezelni - a képesség hiányában csendben nem csinálunk semmit.
 *
 * ─── Mit számolunk bele? ────────────────────────────────────────────────────
 *
 * MINDEN olvasatlan üzenetet - ugyanazt a számot, ami az oldalsávban is ott
 * áll a beszélgetések mellett. Így az ikonon látott szám és a felületen
 * összeadható számok mindig egyeznek; ha a kettő mást mondana, egyik sem
 * lenne hihető.
 *
 * A némított beszélgetések kimaradnak - aki lenémított valamit, arról pont
 * azt mondta, hogy ne szóljunk miatta. Ez a felületen is így van: a némított
 * sor nem kap számot.
 */
export function unreadBadgeCount(channels: ChannelWithMembership[] | undefined): number {
  let count = 0;

  for (const channel of channels ?? []) {
    // Az oldalsavbol hianyzo (archivalt, elrejtett) sem: annak nincs hol latszania.
    if (channel.membership.isMuted || !isListedChannel(channel)) continue;
    count += channel.unreadCount;
  }

  return count;
}

/**
 * A tipusdefinicio szerint a `Navigator` mindig tudja - a VALOSAG szerint nem.
 *
 * A Badging API-t a Firefox es a regebbi Safari nem ismeri, ezert futasidoben
 * is meg kell nezni, nem eleg a fordito szava.
 */
function badgeApi(): { set: (n: number) => void; clear: () => void } | null {
  if (typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return null;

  // A hibat elnyeljuk: a jelveny kenyelem, nem funkcio. Ha a rendszer nem
  // engedi (jogosultsag, nem telepitett alkalmazas), az nem torhet el semmit.
  return {
    set: (n) => void navigator.setAppBadge(n).catch(() => undefined),
    clear: () => void navigator.clearAppBadge().catch(() => undefined),
  };
}

export function useAppBadge(count: number): void {
  useEffect(() => {
    const api = badgeApi();
    if (!api) return;

    if (count > 0) api.set(count);
    else api.clear();
  }, [count]);

  // Kilepeskor (vagy a felulet elhagyasakor) ne maradjon ott egy regi szam.
  useEffect(() => () => badgeApi()?.clear(), []);
}
